/**
 * Operator-side payment-method CRUD. The customer-facing public booking
 * page reads the same rows via booqly-public-api; this file owns the
 * write side (create / update / soft-delete + QR upload).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authFetch, authFetchJson } from "./auth-fetch";
import { throwIfNotOk } from "./api-error";
import { useAuthStore } from "./auth";

export type PaymentMethodType = "BANK" | "EWALLET" | "CASH" | "OTHER";

export type PaymentMethod = {
  id: string;
  methodType: PaymentMethodType;
  provider: string;
  displayName: string;
  accountName: string | null;
  accountNumber: string | null;
  /** Presigned MinIO URL — null when no QR is uploaded. 1-day TTL. */
  qrCodeUrl: string | null;
  instructions: string | null;
  active: boolean;
  orderIndex: number;
};

export type PaymentMethodInput = {
  methodType: PaymentMethodType;
  provider?: string;
  displayName: string;
  accountName?: string;
  accountNumber?: string;
  instructions?: string;
  active?: boolean;
};

const KEY = ["payment-methods"] as const;

export function usePaymentMethods() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: KEY,
    enabled: !!accessToken,
    queryFn: () => authFetchJson<PaymentMethod[]>(`/api/v1/payment-methods`),
  });
}

export function useCreatePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PaymentMethodInput) =>
      authFetchJson<PaymentMethod>(`/api/v1/payment-methods`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdatePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<PaymentMethodInput>) =>
      authFetchJson<PaymentMethod>(`/api/v1/payment-methods/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSoftDeletePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/v1/payment-methods/${id}`, {
        method: "DELETE",
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUploadQrCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authFetch(
        `/api/v1/payment-methods/${id}/qr-code`,
        { method: "POST", body: fd },
      );
      await throwIfNotOk(res);
      return (await res.json()) as PaymentMethod;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useClearQrCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(
        `/api/v1/payment-methods/${id}/qr-code`,
        { method: "DELETE" },
      );
      await throwIfNotOk(res);
      return (await res.json()) as PaymentMethod;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** "Cash / Pay in person" for cash; "Provider · ****1234" otherwise.
 *  Used by RecordPaymentDialog (operator-side) for the compact dropdown
 *  label. The Settings table renders the full info instead. */
export function compactMethodLabel(m: PaymentMethod): string {
  if (m.methodType === "CASH") return "Cash / Pay in person";
  const last4 = (m.accountNumber ?? "").replace(/\D/g, "").slice(-4);
  const tail = last4 ? ` · ****${last4}` : "";
  const head = m.provider?.trim() || m.displayName;
  return `${head}${tail}`;
}
